// NOTE: Shared NodeShift Code contracts consumed by web (and later mobile).
// Mirrors the server's code-sessions REST + WebSocket surface — keep in
// lockstep with:
//   apps/nsai/server/src/code-sessions/types/code-session.types.ts
//   apps/nsai/server/src/database/entities/code-session.entity.ts
//   apps/nsai/server/src/database/entities/code-session-event.entity.ts

export enum ECodeSessionStatus {
  QUEUED = "queued",
  RUNNING = "running",
  AWAITING_INPUT = "awaiting_input",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
  CANCELLED = "cancelled",
  TIMED_OUT = "timed_out",
}

// NOTE: NSAI-5518 / NSAI-5783 — mirrors ECodeAgentKind on the server entity.
// Claude Code only speaks to Claude models, so the agent kind constrains the
// model picker rather than just labelling the session.
export enum ECodeAgentKind {
  OPENHANDS = "openhands",
  CLAUDE_CODE = "claude_code",
}

export enum ECodeSessionEventKind {
  STATUS_CHANGE = "status_change",
  USER_MESSAGE = "user_message",
  ASSISTANT_MESSAGE_DELTA = "assistant_message_delta",
  ASSISTANT_MESSAGE_END = "assistant_message_end",
  TOOL_CALL_START = "tool_call_start",
  TOOL_CALL_STDOUT = "tool_call_stdout",
  TOOL_CALL_STDERR = "tool_call_stderr",
  TOOL_CALL_END = "tool_call_end",
  FILE_CHANGE = "file_change",
  TOOL_APPROVAL_REQUEST = "tool_approval_request",
  PR_OPENED = "pr_opened",
  ERROR = "error",
}

export interface ICodeSessionListItem {
  uuid: string;
  status: ECodeSessionStatus;
  repoFullName: string;
  baseBranch: string;
  workingBranch: string | null;
  prompt: string;
  prUrl: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface ICodeSessionDetail extends ICodeSessionListItem {
  agentKind: ECodeAgentKind;
  promptTokens: number | null;
  completionTokens: number | null;
  durationMs: number | null;
  error: string | null;
}

export interface ICodeSessionEvent {
  sequence: number;
  kind: ECodeSessionEventKind;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface ICodeSessionEventsPage {
  items: ICodeSessionEvent[];
  lastSequence: number | null;
}

// NOTE: NSAI-3720 — the repository and branch a session can be started
// against, read through the GitHub integration the user already connected
// under Integrations. Deliberately a trimmed projection of GitHub's payload:
// the composer needs a name, a default branch and a privacy marker, and
// nothing else should cross the wire.
export interface ICodeRepository {
  fullName: string;
  owner: string;
  name: string;
  defaultBranch: string;
  isPrivate: boolean;
}

export interface ICodeBranch {
  name: string;
  isProtected: boolean;
}

// NOTE: A connected GitHub account, by the display title the Integrations page
// gave it. A user may connect more than one, and nothing here may guess which
// of them a coding session should read — the picker asks.
export interface ICodeGithubAccount {
  title: string;
}

// NOTE: `isConnected: false` is a normal answer, not an error: it is how the
// composer knows to point the user at Integrations instead of showing an empty
// picker that looks broken. `repositories` is likewise empty-but-fine when more
// than one account is connected and the caller named none — `accounts` then
// tells the picker what to offer.
export interface ICodeRepositoriesResponse {
  isConnected: boolean;
  accounts: ICodeGithubAccount[];
  repositories: ICodeRepository[];
}

export interface ICodeBranchesResponse {
  branches: ICodeBranch[];
}

// NOTE: `accountTitle` names which connected GitHub account to read, by its
// Integrations display title. It is only needed when the user connected more
// than one — with a single account the server resolves it and never guesses.
export interface IGetCodeRepositoriesParams {
  accountTitle?: string;
}

export interface IGetCodeBranchesParams extends IGetCodeRepositoriesParams {
  owner: string;
  repo: string;
}

export interface IGetCodeSessionListParams {
  limit: number;
  offset: number;
}

export interface IGetCodeSessionEventsParams {
  uuid: string;
  afterSequence?: number;
  limit?: number;
}

export interface ICreateCodeSessionRequest {
  repoOwner: string;
  repoName: string;
  baseBranch: string;
  prompt: string;
  agentKind?: ECodeAgentKind;
  modelUuid?: string;
  timeoutSeconds?: number;
  maxSteps?: number;
}

export interface ICreateCodeSessionParams {
  payload: ICreateCodeSessionRequest;
  idempotencyKey?: string;
}

// NOTE: WebSocket wire contract for the /api/code-sessions namespace.
// Mirrors the payloads in code-sessions.gateway.ts.
export interface ISubscribeSessionPayload {
  sessionUuid: string;
  lastSequence?: number;
}

export interface ICodeSessionReplayMessage {
  sessionUuid: string;
  events: ICodeSessionEvent[];
  lastSequence: number | null;
}

export interface ICodeSessionLiveEvent extends ICodeSessionEvent {
  sessionUuid: string;
}
